import { useEffect, useMemo, useState } from 'react';
import { CloseButton, Modal, Text, TextInput } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGrid2Plus,
  faMagnifyingGlass,
  faSliders,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { AppsPane } from './AppsPane';
import { PersonalizationPane } from './PersonalizationPane';
import styles from './MooSettingsModal.module.css';
import type_ from './settingsType.module.css';

export type MooSettingsPane = 'personalization' | 'apps';

interface NavItem {
  readonly id: MooSettingsPane;
  readonly label: string;
  readonly icon: IconDefinition;
  /** Matched against the rail's search box, so a term can find a pane by its
   *  contents rather than only by its label. */
  readonly keywords: readonly string[];
}

const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'personalization',
    label: 'Personalization',
    icon: faSliders,
    keywords: ['memory', 'memories', 'tone', 'tones', 'voice', 'style'],
  },
  {
    id: 'apps',
    label: 'Apps',
    icon: faGrid2Plus,
    keywords: ['connect', 'connected', 'integration', 'integrations', 'oauth'],
  },
];

export interface MooSettingsModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  /** Pane to open on. The popover's app entry points open straight to Apps. */
  readonly initialPane?: MooSettingsPane;
  /** Closes the dialog and routes. Studio links leave the conversation. */
  readonly onNavigate?: (path: string) => void;
}

/**
 * OMNI Settings, opened from the composer.
 *
 * Deliberately a modal rather than a route: the composer's popover only has
 * room for a compact list, and pulling someone out of their conversation to
 * manage a connection loses their place.
 *
 * This is the user's own settings. It must never link into Moo Studio →
 * Integrations, which is tenant configuration and root:root only.
 */
export function MooSettingsModal({
  opened,
  onClose,
  initialPane = 'apps',
  onNavigate,
}: MooSettingsModalProps) {
  const [pane, setPane] = useState<MooSettingsPane>(initialPane);
  const [search, setSearch] = useState('');

  // ConnectedAppsBridge keeps this modal mounted across open/close, so
  // useState's initial value is only ever read once. Without this, someone who
  // visits Personalization and closes reopens on Personalization — even via
  // "Connect more", which explicitly asks for the Apps pane. The stale search
  // term is the same bug: the rail comes back still filtered.
  useEffect(() => {
    if (!opened) return;
    setPane(initialPane);
    setSearch('');
  }, [opened, initialPane]);

  const visibleNav = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return NAV_ITEMS;
    return NAV_ITEMS.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        item.keywords.some((k) => k.includes(term))
    );
  }, [search]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      // The pane supplies its own title, so the Mantine header would duplicate
      // it — keep only the close button.
      title={null}
      size={1100}
      centered
      // The AskMoo widget sits at z-index 1100; a default-z modal opened from
      // inside it renders behind the widget.
      zIndex={1400}
      // A global app style resolves the modal inner's inset-inline-start to
      // 100%, pushing the dialog a viewport-width right so only the overlay
      // shows.
      withCloseButton={false}
      styles={{
        inner: { left: 0, right: 0 },
        body: { padding: 0 },
        content: { overflow: 'hidden' },
        header: { display: 'none' },
      }}
    >
      <div className={styles.layout}>
        <nav className={styles.rail} aria-label="Settings sections">
          <TextInput
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search settings"
            aria-label="Search settings"
            leftSection={<FontAwesomeIcon icon={faMagnifyingGlass} />}
            className={styles.railSearch}
          />
          <div className={styles.navList}>
            {visibleNav.map((item) => (
              <button
                key={item.id}
                type="button"
                className={
                  item.id === pane
                    ? `${styles.navItem} ${styles.navItemActive}`
                    : styles.navItem
                }
                aria-current={item.id === pane ? 'page' : undefined}
                onClick={() => setPane(item.id)}
              >
                <FontAwesomeIcon icon={item.icon} fixedWidth />
                {item.label}
              </button>
            ))}
            {visibleNav.length === 0 && (
              <Text className={`${type_.subtitle} ${styles.navEmpty}`}>
                No settings match “{search.trim()}”.
              </Text>
            )}
          </div>
        </nav>

        <div className={styles.pane}>
          <div className={styles.paneHead}>
            <Text component="h2" className={type_.dialogTitle}>
              OMNI Settings
            </Text>
            <CloseButton onClick={onClose} aria-label="Close settings" />
          </div>
          {pane === 'apps' && <AppsPane />}
          {pane === 'personalization' && (
            <PersonalizationPane
              onNavigate={onNavigate}
              onOpenApps={() => setPane('apps')}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
